import { Card } from '../../components/ui/Card';
import styles from './ClaimConstraints.module.css';

/**
 * Portfolio-level limits, reproduced from the guide's "Constraints that apply
 * across every claim" panel.
 *
 * These deliberately sit outside the engine. Every one of them is measured across
 * Exigo's whole claim book — the pre-sales to post-sales ratio, the success rate,
 * the regional caps, the concurrent-claim limit — and this tool can only see one
 * scenario. It cannot see claim history, so it lists them rather than testing
 * them, exactly as the legacy tool did.
 *
 * Nothing here is computed. Do not add a calculated figure to this panel without
 * a source for the claim history it would need.
 */
export function ClaimConstraints(): React.JSX.Element {
  return (
    <Card className={styles.card}>
      <p className={styles.title}>Constraints that apply across every claim</p>

      <ul className={styles.list}>
        <li>
          <b>MCI earns on year 1 only.</b> A three-year contract does not triple the incentive.
          Sizing is always the planned incremental consumption or seat growth for the 12 months from
          claim submission.
        </li>
        <li>
          <b>Azure pre-sales to post-sales ratio is capped at 2:1.</b> Approved post-sales claims
          score 1 point, in-progress 0.5. Exceed the ratio and pre-sales claims are paused after
          30 days&rsquo; notice.
        </li>
        <li>
          <b>Azure post-sales needs a 60% success rate.</b> At least 60% of claims must reach their
          minimum incremental ACR. Months 4&ndash;6 need 75% of minimum, months 7&ndash;12 need
          100%. Fall below and the whole program pauses.
        </li>
        <li>
          <b>Regional partner earning caps:</b> USD 800K for SMB customers, USD 2M total pre-sales.
        </li>
        <li>
          <b>Security claims:</b> partner-wide limit of 50 concurrent active claims across all
          engagement types.
        </li>
        <li>
          <b>Customer consent expires.</b> 30 days from claiming the customer to obtain consent;
          90 days from consent to submit POE; 30 days for POE validation; payment up to 45 days
          after the claim approval month end.
        </li>
        <li>
          <b>Azure credits and cost-optimisation work reduce measured ACR.</b> Don&rsquo;t claim
          cost-reduction projects. Avoid sponsored, credit-funded or masked subscription IDs.
        </li>
        <li>
          <b>Subscription IDs can be corrected once</b> per claim, via the Post-Approval
          Subscription ID Change Form from the Azure Partner Offering alias.
        </li>
        <li>
          <b>Not covered here:</b> CSP transactional incentives (Azure, M365, D365 consumption and
          growth accelerators), the Hosting incentive, SPLA-Reseller, the Enterprise incentive and
          Biz Apps Presales Advisor. Their rates sit in the MCI Product Addendum rate card, not in
          this guide.
        </li>
      </ul>

      <div className={styles.warn}>
        <b>Before you share this.</b> It reproduces Microsoft Confidential material. Share it only
        with people covered by Exigo&rsquo;s Microsoft AI Cloud Partner Program Agreement — not
        customers, subcontractors or distributors.
      </div>
    </Card>
  );
}
